import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, Star, X } from 'lucide-react';
import { supabase } from '../config/supabase';
import { useLanguage } from '../i18n/LanguageContext';
import { trackEvent } from '../analytics';

// Floating feedback button + dialog. Rows go to the insert-only
// `configurator_feedback` table (migration 022) together with a snapshot of
// what the visitor was configuring, supplied by `getContext`.
interface Props {
  getContext?: () => { step?: number; config?: Record<string, unknown> };
  className?: string;
}

const GOLD = '#dfa926';
const BORDER = 'rgba(255,255,255,0.12)';

const FeedbackButton: React.FC<Props> = ({ getContext, className }) => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const textRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    textRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => {
    setOpen(false);
    if (status === 'sent') {
      setRating(null); setMessage(''); setEmail(''); setStatus('idle');
    }
  };

  const canSend = (rating !== null || message.trim().length > 0) && status !== 'sending';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    setStatus('sending');
    const ctx = getContext?.() || {};
    const { error } = await supabase.from('configurator_feedback').insert({
      rating,
      message: message.trim().slice(0, 2000) || null,
      email: email.trim().slice(0, 254) || null,
      lang,
      page_path: window.location.pathname.slice(0, 200),
      step: ctx.step ?? null,
      config: ctx.config ?? null,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      user_agent: navigator.userAgent.slice(0, 400)
    });
    if (error) {
      setStatus('error');
      return;
    }
    setStatus('sent');
    trackEvent('configurator_feedback_submit', { rating: rating ?? 0, has_message: message.trim().length > 0 });
  };

  const input: React.CSSProperties = {
    width: '100%', background: 'rgba(17,17,18,0.7)', border: `1px solid ${BORDER}`,
    borderRadius: '0.55rem', color: '#f9fafb', padding: '0.6rem 0.75rem', fontSize: '0.9rem'
  };

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => { setOpen(true); trackEvent('configurator_feedback_open', {}); }}
        aria-haspopup="dialog"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.45rem 0.85rem', borderRadius: '999px',
          border: `1px solid ${BORDER}`, background: 'rgba(17,17,18,0.85)',
          color: '#d1d5db', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
          backdropFilter: 'blur(4px)', boxShadow: '0 6px 18px rgba(0,0,0,0.35)'
        }}
      >
        <MessageSquare size={15} color={GOLD} />
        {t('configurator.feedback.button')}
      </button>

      {open && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cfg-feedback-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '26rem', background: '#151517',
              border: `1px solid ${BORDER}`, borderRadius: '1rem', padding: '1.25rem',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5)', position: 'relative', textAlign: 'left'
            }}
          >
            <button
              type="button"
              onClick={close}
              aria-label={t('configurator.feedback.close')}
              style={{ position: 'absolute', top: '0.8rem', right: '0.8rem', background: 'none', border: 0, color: '#9ca3af', cursor: 'pointer' }}
            >
              <X size={18} />
            </button>
            <h2 id="cfg-feedback-title" style={{ color: '#f9fafb', fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.35rem' }}>
              {t('configurator.feedback.title')}
            </h2>

            {status === 'sent' ? (
              <>
                <p style={{ color: '#d1d5db', fontSize: '0.9rem', lineHeight: 1.55, margin: '0.5rem 0 1rem' }}>
                  {t('configurator.feedback.thanks')}
                </p>
                <button
                  type="button"
                  onClick={close}
                  style={{ ...input, width: 'auto', cursor: 'pointer', fontWeight: 700, background: GOLD, color: '#111', borderColor: GOLD }}
                >
                  {t('configurator.feedback.close')}
                </button>
              </>
            ) : (
              <form onSubmit={submit}>
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', lineHeight: 1.5, margin: '0 0 0.9rem' }}>
                  {t('configurator.feedback.intro')}
                </p>
                <div role="radiogroup" aria-label={t('configurator.feedback.ratingLabel')} style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.9rem' }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={rating === n}
                      aria-label={`${n}/5`}
                      onClick={() => setRating(rating === n ? null : n)}
                      style={{ background: 'none', border: 0, padding: '0.15rem', cursor: 'pointer' }}
                    >
                      <Star size={26} color={GOLD} fill={rating !== null && n <= rating ? GOLD : 'none'} />
                    </button>
                  ))}
                </div>
                <label htmlFor="cfg-feedback-msg" style={{ display: 'block', color: '#9ca3af', fontSize: '0.75rem', marginBottom: '0.3rem' }}>
                  {t('configurator.feedback.messageLabel')}
                </label>
                <textarea
                  id="cfg-feedback-msg"
                  ref={textRef}
                  rows={4}
                  maxLength={2000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('configurator.feedback.messagePh')}
                  style={{ ...input, resize: 'vertical', marginBottom: '0.75rem' }}
                />
                <label htmlFor="cfg-feedback-email" style={{ display: 'block', color: '#9ca3af', fontSize: '0.75rem', marginBottom: '0.3rem' }}>
                  {t('configurator.feedback.emailLabel')}
                </label>
                <input
                  id="cfg-feedback-email"
                  type="email"
                  maxLength={254}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('configurator.feedback.emailPh')}
                  style={{ ...input, marginBottom: '1rem' }}
                />
                {status === 'error' && (
                  <p role="alert" style={{ color: '#f87171', fontSize: '0.82rem', margin: '0 0 0.75rem' }}>
                    {t('configurator.feedback.error')}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={!canSend}
                  style={{
                    ...input, cursor: canSend ? 'pointer' : 'not-allowed', fontWeight: 700,
                    background: canSend ? GOLD : 'rgba(223,169,38,0.35)', color: '#111', borderColor: GOLD
                  }}
                >
                  {status === 'sending' ? t('configurator.feedback.sending') : t('configurator.feedback.send')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
